// WebMCP tools for the Vibing Library (2,000+ curated vibe-coding resources).
//
// Search and filtering run entirely in the tab against the index the page has
// already loaded, so an agent gets sub-millisecond answers with no extra
// network round trips.

import {
  filterGems,
  gemSummary,
  loadLibraryData,
  SITE_ORIGIN,
} from "../library-data";
import { errorResult, jsonResult, textResult } from "../types";
import type { WebMcpTool } from "../types";
import type { LibraryGem } from "@/lib/library-types";
import { isLiked, toggleLike } from "@/lib/library-engagement";

export type LibraryToolDeps = {
  navigate: (to: string) => void;
  /** Drive the on-page search UI so the human sees what the agent did. */
  applyFilters?: (f: {
    query?: string;
    category?: string | null;
    subcategory?: string | null;
    sort?: "relevance" | "name" | "views" | "likes" | "new";
  }) => void;
};

const SORTS = ["relevance", "name", "views", "likes", "new"] as const;

async function detail(slug: string) {
  const res = await fetch(`/api/public/library/gems/${encodeURIComponent(slug)}`);
  if (!res.ok) return null;
  return (await res.json()) as Record<string, unknown>;
}

function related(gem: LibraryGem, all: LibraryGem[], limit: number) {
  const tags = new Set(gem.tags.map((t) => t.toLowerCase()));
  return all
    .filter((g) => g.slug !== gem.slug)
    .map((g) => {
      let score = 0;
      if (g.subcategory === gem.subcategory) score += 4;
      if (g.category === gem.category) score += 2;
      for (const t of g.tags) if (tags.has(t.toLowerCase())) score += 3;
      return { g, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || b.g.likes - a.g.likes)
    .slice(0, limit)
    .map((x) => gemSummary(x.g));
}

export function createLibraryTools(deps: LibraryToolDeps): WebMcpTool[] {
  return [
    {
      name: "library_search",
      title: "Search the Vibing Library",
      description:
        "Search 2,000+ hand-curated vibe-coding resources — dev tools, UI components, AI skills, shaders, galleries and docs. Filter by category, subcategory, tag, author or license and sort by relevance, popularity or recency.",
      inputSchema: {
        type: "object",
        properties: {
          query: { type: "string", description: "Free-text query, e.g. 'react animation library'" },
          category: { type: "string", description: "Exact or partial category name" },
          subcategory: { type: "string", description: "Exact or partial subcategory name" },
          tag: { type: "string", description: "Exact tag, e.g. 'webgpu'" },
          author: { type: "string", description: "Creator name" },
          license: { type: "string", description: "License, e.g. 'MIT'" },
          sort: { type: "string", enum: [...SORTS] },
          limit: { type: "number", description: "1-50, default 10" },
          offset: { type: "number", description: "Result offset for paging" },
          reflectInUi: {
            type: "boolean",
            description: "Also apply the query to the visible search UI (default true on the library page)",
          },
        },
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true, idempotentHint: true, untrustedContentHint: true },
      execute: async (input) => {
        const { gems } = await loadLibraryData();
        const sort = SORTS.includes(input.sort as never)
          ? (input.sort as (typeof SORTS)[number])
          : "relevance";
        const { total, items } = filterGems(gems, {
          query: input.query as string | undefined,
          category: input.category as string | undefined,
          subcategory: input.subcategory as string | undefined,
          tag: input.tag as string | undefined,
          author: input.author as string | undefined,
          license: input.license as string | undefined,
          sort,
          limit: Number(input.limit ?? 10),
          offset: Number(input.offset ?? 0),
        });
        if (input.reflectInUi !== false) {
          deps.applyFilters?.({
            query: (input.query as string) ?? "",
            category: (input.category as string) ?? null,
            subcategory: (input.subcategory as string) ?? null,
            sort,
          });
        }
        return jsonResult(
          { total, returned: items.length, results: items.map(gemSummary) },
          `${total} matching gems; showing ${items.length}.`,
        );
      },
    },
    {
      name: "library_get_gem",
      title: "Get one library gem",
      description:
        "Get the full record for a single library gem by slug: description, why it is a gem, canonical URL, tags, license, creator and stats.",
      inputSchema: {
        type: "object",
        properties: { slug: { type: "string" } },
        required: ["slug"],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true, idempotentHint: true, untrustedContentHint: true },
      execute: async (input) => {
        const slug = String(input.slug ?? "").trim();
        if (!slug) return errorResult("slug is required");
        const full = await detail(slug);
        if (full) return jsonResult(full, `Gem: ${String(full.name ?? slug)}`);
        const { gems } = await loadLibraryData();
        const gem = gems.find((g) => g.slug === slug);
        return gem
          ? jsonResult(gemSummary(gem), `Gem: ${gem.name}`)
          : errorResult(`No gem found with slug "${slug}"`);
      },
    },
    {
      name: "library_list_categories",
      title: "List library categories",
      description:
        "List every category in the Vibing Library with how many gems it holds, plus the subcategories inside each one.",
      inputSchema: {
        type: "object",
        properties: { category: { type: "string", description: "Limit to one category" } },
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true, idempotentHint: true },
      execute: async (input) => {
        const { gems, categories } = await loadLibraryData();
        const only = input.category ? String(input.category).toLowerCase() : null;
        const byCat = new Map<string, Map<string, number>>();
        for (const g of gems) {
          if (only && g.category.toLowerCase() !== only) continue;
          const subs = byCat.get(g.category) ?? new Map<string, number>();
          if (g.subcategory) subs.set(g.subcategory, (subs.get(g.subcategory) ?? 0) + 1);
          byCat.set(g.category, subs);
        }
        const value = [...byCat.entries()].map(([name, subs]) => ({
          name,
          count: categories.find((c) => c.name === name)?.count ?? gems.filter((g) => g.category === name).length,
          subcategories: [...subs.entries()]
            .map(([sub, count]) => ({ name: sub, count }))
            .sort((a, b) => b.count - a.count),
        }));
        return jsonResult(value, `${value.length} categories`);
      },
    },
    {
      name: "library_list_tags",
      title: "List library tags",
      description:
        "List the most common tags in the library with counts — useful for narrowing a search before calling library_search.",
      inputSchema: {
        type: "object",
        properties: { limit: { type: "number", description: "Default 60, max 300" } },
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true, idempotentHint: true },
      execute: async (input) => {
        const { gems } = await loadLibraryData();
        const counts = new Map<string, number>();
        for (const g of gems) for (const t of g.tags) counts.set(t, (counts.get(t) ?? 0) + 1);
        const limit = Math.min(Math.max(1, Number(input.limit ?? 60)), 300);
        const value = [...counts.entries()]
          .sort((a, b) => b[1] - a[1])
          .slice(0, limit)
          .map(([name, count]) => ({ name, count }));
        return jsonResult(value, `${counts.size} distinct tags; top ${value.length} returned.`);
      },
    },
    {
      name: "library_recommend",
      title: "Recommend gems for a task",
      description:
        "Describe what the user is building or stuck on and get the most relevant library gems, ranked, with why each one fits.",
      inputSchema: {
        type: "object",
        properties: {
          need: { type: "string", description: "What the user is trying to do" },
          limit: { type: "number", description: "1-20, default 6" },
        },
        required: ["need"],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async (input) => {
        const need = String(input.need ?? "").trim();
        if (!need) return errorResult("need is required");
        const { gems } = await loadLibraryData();
        const limit = Math.min(Math.max(1, Number(input.limit ?? 6)), 20);
        const words = need
          .toLowerCase()
          .split(/[^a-z0-9+.#]+/)
          .filter((w) => w.length > 2)
          .slice(0, 8);
        // Union scoring: unlike search, a partial match still counts.
        const seen = new Map<string, { gem: LibraryGem; hits: number }>();
        for (const word of words) {
          const { items } = filterGems(gems, { query: word, limit: 50 });
          for (const gem of items) {
            const prev = seen.get(gem.slug);
            if (prev) prev.hits += 1;
            else seen.set(gem.slug, { gem, hits: 1 });
          }
        }
        const ranked = [...seen.values()]
          .sort((a, b) => b.hits - a.hits || b.gem.likes - a.gem.likes)
          .slice(0, limit)
          .map(({ gem, hits }) => ({ ...gemSummary(gem), matchedTerms: hits }));
        return jsonResult(
          { need, recommendations: ranked },
          ranked.length
            ? `${ranked.length} recommended gems for "${need}".`
            : `No library gems matched "${need}".`,
        );
      },
    },
    {
      name: "library_related_gems",
      title: "Find related gems",
      description:
        "Given a gem slug, return other library gems in the same space (shared tags, subcategory or category).",
      inputSchema: {
        type: "object",
        properties: {
          slug: { type: "string" },
          limit: { type: "number", description: "1-20, default 6" },
        },
        required: ["slug"],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true, idempotentHint: true, untrustedContentHint: true },
      execute: async (input) => {
        const { gems } = await loadLibraryData();
        const gem = gems.find((g) => g.slug === String(input.slug));
        if (!gem) return errorResult(`No gem found with slug "${String(input.slug)}"`);
        const limit = Math.min(Math.max(1, Number(input.limit ?? 6)), 20);
        return jsonResult(related(gem, gems, limit), `Gems related to ${gem.name}`);
      },
    },
    {
      name: "library_compare_gems",
      title: "Compare gems side by side",
      description:
        "Compare two to five library gems on category, tags, license, creator and popularity.",
      inputSchema: {
        type: "object",
        properties: {
          slugs: { type: "array", items: { type: "string" }, description: "2-5 gem slugs" },
        },
        required: ["slugs"],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true, idempotentHint: true, untrustedContentHint: true },
      execute: async (input) => {
        const slugs = Array.isArray(input.slugs) ? input.slugs.map(String).slice(0, 5) : [];
        if (slugs.length < 2) return errorResult("Provide between 2 and 5 slugs");
        const { gems } = await loadLibraryData();
        const found = slugs
          .map((s) => gems.find((g) => g.slug === s))
          .filter((g): g is LibraryGem => Boolean(g));
        if (found.length < 2) return errorResult("Fewer than two of those slugs exist");
        return jsonResult(
          {
            comparison: found.map(gemSummary),
            sharedTags: [
              ...found
                .map((g) => new Set(g.tags.map((t) => t.toLowerCase())))
                .reduce((acc, set) => new Set([...acc].filter((t) => set.has(t)))),
            ],
          },
          `Comparing ${found.map((g) => g.name).join(" vs ")}`,
        );
      },
      // structuredContent must be JSON-serialisable; Sets are not.
    },
    {
      name: "library_open_gem",
      title: "Open a gem page",
      description:
        "Navigate the browser to a gem's page on vibing.inc so the user can see the preview, tags and link.",
      inputSchema: {
        type: "object",
        properties: { slug: { type: "string" } },
        required: ["slug"],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false },
      execute: async (input) => {
        const slug = String(input.slug ?? "");
        const { gems } = await loadLibraryData();
        if (!gems.some((g) => g.slug === slug)) return errorResult(`Unknown gem slug "${slug}"`);
        deps.navigate(`/library/${slug}`);
        return textResult(`Opened ${SITE_ORIGIN}/library/${slug}`);
      },
    },
    {
      name: "library_like_gem",
      title: "Like or unlike a gem",
      description:
        "Save a gem to this browser's likes (or remove it). Only call this when the user asked to save or unsave a gem.",
      inputSchema: {
        type: "object",
        properties: {
          slug: { type: "string" },
          liked: { type: "boolean", description: "true to like, false to unlike. Defaults to true." },
        },
        required: ["slug"],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false, destructiveHint: false },
      execute: async (input) => {
        const slug = String(input.slug ?? "");
        if (!slug) return errorResult("slug is required");
        const liked = input.liked === undefined ? true : Boolean(input.liked);
        const total = await toggleLike(slug, liked);
        return jsonResult(
          { slug, liked, likes: total },
          `${liked ? "Liked" : "Unliked"} ${slug}${total === null ? "" : ` (${total} likes)`}`,
        );
      },
    },
    {
      name: "library_list_liked",
      title: "List gems this browser liked",
      description: "Return the gems the current visitor has saved in this browser.",
      inputSchema: { type: "object", properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true },
      execute: async () => {
        const { gems } = await loadLibraryData();
        const liked = gems.filter((g) => isLiked(g.slug)).map(gemSummary);
        return jsonResult(liked, `${liked.length} liked gems in this browser`);
      },
    },
    {
      name: "library_submit_gem",
      title: "Suggest a resource for the library",
      description:
        "Send a resource suggestion to the Vibing Library review queue. Nothing is published automatically — a human curator reviews every submission. Confirm the URL with the user first.",
      inputSchema: {
        type: "object",
        properties: {
          url: { type: "string", description: "Canonical URL of the resource (docs or repo, not a marketing page)" },
          name: { type: "string", description: "Resource name" },
          description: { type: "string", description: "One-sentence description, max 400 chars" },
          reason: { type: "string", description: "Why it belongs in a vibe-coding library, max 400 chars" },
          category: { type: "string" },
          tags: { type: "array", items: { type: "string" }, description: "Up to 12 tags" },
          contactEmail: { type: "string", description: "Optional email for follow-up" },
        },
        required: ["url", "name"],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false, destructiveHint: false, openWorldHint: true },
      execute: async (input) => {
        const url = String(input.url ?? "").trim();
        const name = String(input.name ?? "").trim();
        if (!/^https?:\/\/\S+\.\S+/i.test(url)) return errorResult("A valid http(s) URL is required");
        if (name.length < 2) return errorResult("name is required (2+ characters)");
        try {
          const res = await fetch("/api/public/library/submit", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              url,
              name: name.slice(0, 120),
              description: input.description ? String(input.description).slice(0, 400) : undefined,
              whyGem: input.reason ? String(input.reason).slice(0, 400) : undefined,
              category: input.category ? String(input.category).slice(0, 80) : undefined,
              tags: Array.isArray(input.tags)
                ? input.tags.map(String).filter(Boolean).slice(0, 12)
                : undefined,
              contactEmail: input.contactEmail ? String(input.contactEmail) : undefined,
              source: "webmcp",
              submittedBy: "agent",
            }),
          });
          const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
          if (!res.ok) {
            const e = body.error as { message?: string } | undefined;
            return errorResult(
              `Submission rejected (${res.status}): ${e?.message ?? "unknown error"}`,
            );
          }
          return jsonResult(
            { queued: true, url, ...body },
            "Queued for human review — nothing is published automatically.",
          );
        } catch {
          return errorResult("Could not reach the submission endpoint");
        }
      },
    },
  ];
}
